import { useEffect, useRef, useState } from 'react';
import { QUANTUM_SURGE_REFRESH_DURATION_MS } from '../config/quantumSurgeRefreshEffect';
import { playSoundEffect } from '../hooks/useSoundEffects';

function mapAttackAgainFxToViewerSlot(fx, viewerUserId) {
  if (!fx || fx.zone == null || fx.slotIndex == null || fx.targetPlayerId == null) {
    return null;
  }
  const isOwnerViewer = String(fx.targetPlayerId) === String(viewerUserId);
  const prefix = isOwnerViewer ? 'your' : 'opponent';
  return {
    zone: `${prefix}_${fx.zone}`,
    index: fx.slotIndex,
  };
}

const QUANTUM_SURGE_STRATEGEM_ID = 187;

function isQuantumSurgeStrategem(s) {
  if (!s || s.instanceId == null) return false;
  if (Number(s.id) !== QUANTUM_SURGE_STRATEGEM_ID) return false;
  const t = (s.type && String(s.type)) || '';
  if (!t) return true;
  return /strategem/i.test(t);
}

function hasAttackAgainBattlefieldFx(unit) {
  if (!unit?.instanceId) return false;
  const fx = unit.attackAgainFx;
  return (
    fx != null
    && fx.zone != null
    && fx.slotIndex != null
    && typeof fx.pulseSeq === 'number'
    && fx.pulseSeq > 0
  );
}

function attackAgainFxDedupeKey(ownerId, unit) {
  const pulseSeq = unit.attackAgainFx?.pulseSeq ?? 0;
  return `aa-${ownerId}-${unit.instanceId}-${pulseSeq}`;
}

function queueAttackAgainRefresh(viewerSlot, setActiveQuantumSurgeRefreshes, soundKeyPrefix, instanceId, playSound = false) {
  const animKey = `${soundKeyPrefix}-${instanceId}-${Date.now()}`;
  setActiveQuantumSurgeRefreshes((prev) => [
    ...prev,
    { key: animKey, viewerSlot, animationKey: animKey },
  ]);
  if (playSound) {
    playSoundEffect('/sounds/surge.mp3', 0.6);
  }
  window.setTimeout(() => {
    setActiveQuantumSurgeRefreshes((prev) => prev.filter((p) => p.key !== animKey));
  }, QUANTUM_SURGE_REFRESH_DURATION_MS + 120);
}

/**
 * attack_again.png on unit slot — Strategem 187 (quantumSurgeFx) and raid units like 176 (attackAgainFx).
 */
export function useQuantumSurgeRefresh(gameState, viewerUserId) {
  const [activeQuantumSurgeRefreshes, setActiveQuantumSurgeRefreshes] = useState([]);
  const processedRef = useRef(new Set());
  const seededRef = useRef(false);
  const gameIdRef = useRef(null);

  useEffect(() => {
    if (!gameState?.state || viewerUserId == null) return;

    const gid = gameState.gameId ?? null;
    if (gid != null && gameIdRef.current !== gid) {
      gameIdRef.current = gid;
      seededRef.current = false;
      processedRef.current = new Set();
      setActiveQuantumSurgeRefreshes([]);
    }

    if (!seededRef.current) {
      Object.keys(gameState.state).forEach((pid) => {
        gameState.state[pid]?.completedOrders?.forEach((st) => {
          if (isQuantumSurgeStrategem(st)) {
            processedRef.current.add(`qs187-${pid}-${st.instanceId}`);
          }
        });
        const player = gameState.state[pid];
        ['front', 'flank'].forEach((zone) => {
          (player?.playedOrders?.[zone] || []).forEach((unit) => {
            if (hasAttackAgainBattlefieldFx(unit)) {
              processedRef.current.add(attackAgainFxDedupeKey(pid, unit));
            }
          });
        });
      });
      seededRef.current = true;
      return;
    }

    Object.keys(gameState.state).forEach((casterId) => {
      const player = gameState.state[casterId];
      if (!player?.completedOrders) return;

      player.completedOrders.forEach((strategem) => {
        if (!isQuantumSurgeStrategem(strategem)) return;
        const fx = strategem.quantumSurgeFx;
        if (!fx) return;

        const dedupeKey = `qs187-${casterId}-${strategem.instanceId}`;
        if (processedRef.current.has(dedupeKey)) return;
        processedRef.current.add(dedupeKey);

        const viewerSlot = mapAttackAgainFxToViewerSlot(fx, viewerUserId);
        if (!viewerSlot) return;

        queueAttackAgainRefresh(viewerSlot, setActiveQuantumSurgeRefreshes, 'qs187', strategem.instanceId);
      });
    });

    Object.keys(gameState.state).forEach((ownerId) => {
      const player = gameState.state[ownerId];
      if (!player?.playedOrders) return;

      ['front', 'flank'].forEach((zone) => {
        (player.playedOrders[zone] || []).forEach((unit) => {
          if (!hasAttackAgainBattlefieldFx(unit)) return;
          const fx = unit.attackAgainFx;
          const dedupeKey = attackAgainFxDedupeKey(ownerId, unit);
          if (processedRef.current.has(dedupeKey)) return;
          processedRef.current.add(dedupeKey);

          const viewerSlot = mapAttackAgainFxToViewerSlot(fx, viewerUserId);
          if (!viewerSlot) return;

          queueAttackAgainRefresh(viewerSlot, setActiveQuantumSurgeRefreshes, 'aa', unit.instanceId, true);
        });
      });
    });

    if (processedRef.current.size > 120) {
      const keys = Array.from(processedRef.current);
      keys.slice(0, keys.length - 80).forEach((k) => processedRef.current.delete(k));
    }
  }, [gameState, gameState?.stateVersion, viewerUserId]);

  return { activeQuantumSurgeRefreshes };
}

export default useQuantumSurgeRefresh;
